import Logger = require("js-logger");
import CampaignsModel from "slotworks/integration/revolver/model/campaigns-model";
import RevolverModel from "slotworks/integration/revolver/model/revolver-model";
import { AwardData } from "slotworks/integration/revolver/schema/init-record-schema";
import { ResultsAndDetail, SpinResponseSchema } from "slotworks/integration/revolver/schema/spin-record-schema";
import Gameplay from "slotworks/model/gameplay/gameplay-model";
import FreespinRecord from "integration/revolver/schema/freespin-record";
import SpinRecord from "integration/revolver/schema/spin-record";
import Record from "slotworks/model/gameplay/records/record";
import Result from "slotworks/model/gameplay/records/results/result";
import { ExpansionDefinition } from "slotworks/model/gameplay/records/spin-record";
import SlotDefinition from "slotworks/model/slot-definition";
import BuilderManager from "slotworks/server/builders/builder-manager";
import { RecordBuilder } from "slotworks/server/builders/record-builder";
import SymbolModel from "slotworks/server/symbols/symbol-model";
import Point from "slotworks/utils/geom/point";
import WrapperMediator from "slotworks/integration/revolver/wrapper/wrapper-mediator";
import DSComponents from "components/components";
import gameState from "slotworks/model/game-state";
import RevolverSettings from "model/revolver-settings";

export class ICRecordBuilder implements RecordBuilder {

    public filter(data: any): boolean {
        let spinResponse: SpinResponseSchema;

        if (data && data.data) {
            spinResponse = data.data as SpinResponseSchema;
        }

        return spinResponse && spinResponse.RESULTS !== undefined && (RevolverModel.isSpin(spinResponse.RESULTS.game) || RevolverModel.isFreespin(spinResponse.RESULTS.game));
    }

    public build(gameplay: Gameplay, data: any): Record[] {
        RevolverSettings.isCountupPlayed = false;
        let spinResponse: SpinResponseSchema;

        if (data && data.data) {
            spinResponse = data.data as SpinResponseSchema;
            WrapperMediator.roundStarted(spinResponse.RESULTS.round_id);
            WrapperMediator.spinResponseReceived(spinResponse);
        }

        gameplay.id = spinResponse.RESULTS.round_id.toString();

        const isFreeSpin = RevolverModel.isFreespin(spinResponse.RESULTS.game);

        const win = parseFloat(spinResponse.RESULTS.total_win_cash as any);
        gameplay.totalCreditsWon += win;
        gameState.updateNetPosition(win);

        gameplay.balance = spinResponse.USER.balance;

        const spinRecord = isFreeSpin ? new FreespinRecord() : new SpinRecord();

        const gameData = (spinResponse.RESULTS.eval as any).gameData;
        this.processGameData(gameData, spinRecord);

        const aperture = spinResponse.RESULTS.eval.aperture as string[][];
        this.processAperture(aperture, spinRecord);

        if (isFreeSpin) {
            spinRecord.reelset = SlotDefinition.reelsets.get("freespins");
            gameplay.totalFreespinsCreditsWon += win;
        } else {
            spinRecord.reelset = SlotDefinition.reelsets.get("base");
            gameplay.totalBaseCreditsWon += win;
        }

        spinRecord.creditsWon = parseFloat(spinResponse.RESULTS.total_win_cash as any);
        spinRecord.gameName = spinResponse.RESULTS.game;

        this.processWins(spinRecord, spinResponse);

        if (spinRecord instanceof FreespinRecord) {
            this.processFreespinsRecord(spinRecord, spinResponse);
        }

        this.processOtherResult(spinRecord, spinResponse);

        this.expandingWilds(spinRecord, spinResponse);

        this.stickyWilds(spinRecord, spinResponse);

        if (CampaignsModel.isFreespinsActive()) {
            CampaignsModel.freespins.totalWin += parseFloat(spinResponse.RESULTS.total_win_cash as any);
        }

        if (spinResponse.RESULTS.updated_free_spin_campaign_details) {
            const freespin = spinResponse.RESULTS.updated_free_spin_campaign_details;
            const awardData = typeof freespin.awardData === "string" ? JSON.parse(freespin.awardData) as AwardData : freespin.awardData as AwardData;
            CampaignsModel.freespins.total = awardData.total;
            CampaignsModel.freespins.remaining = awardData.count;
            CampaignsModel.freespins.isComplete = awardData.count === 0;
        }

        this.processPersistedProgress(spinResponse.RESULTS as any, spinRecord);
        // const persistedProgress = (spinResponse.RESULTS as any).persisted_progress;
        // if(persistedProgress) {
        //     const potsValues = persistedProgress.potsValues;            
        // //    DSComponents.coinPots.setPotLevels(potsValues[40]); 
        // }        

        return [spinRecord];
    }

    private processPersistedProgress(data: any, spinRecord: SpinRecord | FreespinRecord) {
        spinRecord.persistedPotsValues = new Map();
        const persistedProgress = data.persisted_progress;
        if (persistedProgress && persistedProgress.potsValues) {
            const potsValues = persistedProgress.potsValues;
            for (let bet in potsValues) {
                const key: number = parseInt(bet, 10);
                spinRecord.persistedPotsValues.set(key, [...potsValues[bet]]);
            }
        }
        if (persistedProgress && persistedProgress.progressiveJackpot) {
            DSComponents.jackpotMeter.setValues(persistedProgress.progressiveJackpot);
        }

    }

    private processGameData(gameData: GameData, spinRecord: SpinRecord) {
        spinRecord.addedCoins = [...gameData.addedCoins];
        spinRecord.potsValues = [...gameData.potsValues];
        spinRecord.potTriggeredFeatures = [...gameData.potTriggeredFeatures];
        spinRecord.instantWin = gameData.instantWin || 0;
        spinRecord.isJackpotWon = gameData.isJackpotWon;
    }

    private processAperture(aperture: string[][], spinRecord: SpinRecord) {
        let trimmed: string[][] = [];
        for (const reel of aperture) {
            trimmed.push([]);
            for (const symbol of reel) {
                trimmed[trimmed.length - 1].push(symbol);
            }
        }

        trimmed.forEach((outer: string[], i: number, grid: string[][]) => {
            // If revolver aperture does not match expected matrix grid dimensions, time off excess symbols
            if (trimmed[i].length !== SlotDefinition.matrixGrid[i]) {
                outer.shift();
                outer.pop();
            }
            outer.forEach((inner: string, j: number, reel: string[]) => {
                reel[j] = SymbolModel.convertSymbolToSlotworks(inner);
            });
        });

        // TODO temporary
        for (let coinIndex = 0; coinIndex < spinRecord.addedCoins.length; coinIndex++) {
            let coins = spinRecord.addedCoins[coinIndex];
            while (coins > 0) {
                let row = 3 - coins;
                spinRecord.underCoinSymbols.set(new Point(coinIndex, row), trimmed[coinIndex][row]);
                trimmed[coinIndex][row] = "COIN";
                coins--;
            }
        }

        spinRecord.grid = trimmed;
    }

    private processWins(spinRecord: SpinRecord, spinResponse: SpinResponseSchema) {
        let results: Result[] = [];

        if (spinResponse.RESULTS.eval.wins !== undefined) {
            for (const detail of spinResponse.RESULTS.eval.wins.detail) {
                const builtResults: Result[] = BuilderManager.buildResult(spinRecord, {
                    detail,
                    results: spinResponse.RESULTS
                } as ResultsAndDetail);
                results = results.concat(builtResults);
            }
        }

        spinRecord.results = spinRecord.results.concat(results);
        let nextState = (spinResponse.RESULTS as any).next_game_state;
        if (nextState && nextState.total) {
            spinRecord.freespinsWon = nextState.total;
        }
        if (spinRecord.freespinsWon > 0 || RevolverModel.isFreespin(spinResponse.RESULTS.next_game)) {
            spinRecord.freespinsTriggered = true;
        }
    }

    private processFreespinsRecord(spinRecord: FreespinRecord, spinResponse: SpinResponseSchema) {
        if (typeof spinResponse.RESULTS.state.total === "undefined") {
            const total = spinResponse.RESULTS.state.remain + spinResponse.RESULTS.state.spins;
            Logger.log("Error: total freespins is undefined. Setting it to " + total);
            spinResponse.RESULTS.state.total = total;
        }

        spinRecord.retrigger = spinResponse.RESULTS.state.is_retrigger;
        spinRecord.freespinsWon = spinResponse.RESULTS.state.retriggers;
        spinRecord.totalFreespins = spinResponse.RESULTS.state.total;
        spinRecord.remainingFreespins = spinResponse.RESULTS.state.remain;
        spinRecord.isFreespinsComplete = spinResponse.RESULTS.state.is_complete;

        if(spinRecord.retrigger === true) {
            RevolverSettings.retriggerPending = true;
        }
    }

    private processOtherResult(spinRecord: SpinRecord, spinResponse: SpinResponseSchema) {
        const results = BuilderManager.buildResult(spinRecord, spinResponse);

        spinRecord.results = spinRecord.results.concat(results);
    }

    private stickyWilds(spinRecord: SpinRecord, spinResponse: SpinResponseSchema) {
        const stickyWilds = spinResponse.RESULTS.state.latestStickedWilds;
        if (stickyWilds && stickyWilds.length) {
            const gridPositions = RevolverModel.convertPositionsToGridPositions(stickyWilds);

            for (const position of gridPositions) {
                spinRecord.grid[position.x][position.y] = "S" + spinRecord.grid[position.x][position.y];
            }
        }
    }

    private expandingWilds(spinRecord: SpinRecord, spinResponse: SpinResponseSchema) {
        const expandingReels = spinResponse.RESULTS.eval.expanding_reels;
        if (expandingReels && expandingReels.length) {

            spinRecord.expansions = [];

            for (const expandingReel of expandingReels) {

                let source;
                const expansions = [];
                const target = new Point(expandingReel + 1, 1);

                for (let y = 0; y < spinRecord.grid[expandingReel].length; y++) {
                    const symbol = spinRecord.grid[expandingReel][y];
                    const symbolDef = SlotDefinition.getSymbolDefinition(symbol);
                    if (symbolDef.wild && !source) {
                        source = new Point(expandingReel + 1, y + 1);
                    }

                    expansions.push(new Point(expandingReel + 1, y + 1));
                }

                const expansionDefinition = new ExpansionDefinition(source, expansions, target);
                spinRecord.expansions.push(expansionDefinition);
            }
        }
    }
}

export default new ICRecordBuilder();

interface GameData {
    addedCoins: number[],
    isJackpotWon: boolean,
    instantWin: number,
    potTriggeredFeatures: string[],
    potsValues: number[]
}